import { useState, useEffect, useMemo, useRef } from 'react';
import {
  BookOpen, Plus, Trash2, Edit2, Save, X, Search, Loader2,
  Smile, Meh, Frown, Sparkles, TrendingUp, TrendingDown, Hash, Link2, ChevronDown,
  Lightbulb, Clock,
} from 'lucide-react';
import {
  listJournalEntries, getJournalEntry, upsertJournalEntry,
  deleteJournalEntry, searchJournal, getJournalRecentTrades,
} from '../services/api';
import { InfoTip } from '../components/ui';

const SOURCES = [
  { id: 'smart_trade', label: 'Smart Trade',  color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { id: 'grid_bot',    label: 'Grid Bot',     color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  { id: 'dca_safety',  label: 'DCA Safety',   color: 'bg-violet-500/10 text-violet-400 border-violet-500/30' },
  { id: 'cex',         label: 'CEX',          color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' },
  { id: 'dex_swap',    label: 'DEX Swap',     color: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30' },
  { id: 'futures',     label: 'Futures',      color: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  { id: 'other',       label: 'Other',        color: 'bg-gray-500/10 text-gray-400 border-gray-500/30' },
];
const SOURCE_BY_ID = Object.fromEntries(SOURCES.map(s => [s.id, s]));

const SENTIMENT_CONFIG = {
  positive: { Icon: Smile, color: 'text-emerald-400', border: 'border-l-emerald-400', bg: 'bg-emerald-500/5' },
  neutral:  { Icon: Meh,   color: 'text-amber-400',   border: 'border-l-amber-400',   bg: 'bg-amber-500/5'  },
  negative: { Icon: Frown, color: 'text-red-400',     border: 'border-l-red-400',     bg: 'bg-red-500/5'    },
};

function highlightMatch(text, q) {
  if (!text || !q) return text || '';
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-400/30 text-amber-300 rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function JournalPage() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [searchMode, setSearchMode] = useState(false); // true when results come from /search

  async function load() {
    setLoading(true);
    try {
      if (search) {
        const res = await searchJournal(search);
        setEntries(res?.data?.matches || []);
        setSearchMode(true);
      } else {
        const res = await listJournalEntries({ source: filter });
        setEntries(res?.data?.entries || []);
        setSearchMode(false);
      }
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function handleDelete(tradeId) {
    if (!confirm('Delete this journal entry?')) return;
    await deleteJournalEntry(tradeId);
    await load();
  }

  // Aggregate stats from current entry list — purely informational header band
  const stats = useMemo(() => {
    const total = entries.length;
    const profitable = entries.filter(e => (e.realizedPnl ?? 0) > 0).length;
    const losing = entries.filter(e => (e.realizedPnl ?? 0) < 0).length;
    const totalPnl = entries.reduce((s, e) => s + (Number(e.realizedPnl) || 0), 0);
    const journaledRate = total > 0 ? Math.round((profitable / total) * 100) : null;
    return { total, profitable, losing, totalPnl, journaledRate };
  }, [entries]);

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 via-dark-800/40 to-dark-800/40 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-amber-400/5 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <BookOpen className="w-5 h-5 text-dark-900" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Trading Journal</h1>
              <p className="text-xs text-emerald-300/80 mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Personal · Searchable · Cross-system
                <InfoTip side="bottom">Sentiment = PROCESS (did you follow plan), not OUTCOME (did you make money). Write notes within 1 hour of close — cold-take journaling is fiction.</InfoTip>
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-dark-900 font-semibold text-sm px-4 py-2.5 rounded-xl shadow-lg shadow-emerald-500/20"
          >
            <Plus className="w-4 h-4" /> New Entry
          </button>
        </div>

        {/* Stats band */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 relative">
          <HeroStat icon={BookOpen}     label="Entries"        value={stats.total} accent="text-emerald-400" />
          <HeroStat icon={TrendingUp}   label="Wins logged"    value={stats.profitable} accent="text-emerald-400" />
          <HeroStat icon={TrendingDown} label="Losses logged"  value={stats.losing} accent="text-red-400" />
          <HeroStat
            icon={Sparkles}
            label="Net P&L (journaled)"
            value={`${stats.totalPnl >= 0 ? '+' : ''}$${Math.abs(stats.totalPnl).toFixed(2)}`}
            accent={stats.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}
          />
        </div>
      </div>

      {/* ─── Search + filter chips ────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search full notes, lessons, symbols, tags…"
            className="w-full bg-dark-800/60 border border-dark-700/50 rounded-xl pl-10 pr-4 py-3 text-white text-sm focus:border-emerald-500/40 focus:outline-none transition-colors"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          <FilterChip active={filter === null} onClick={() => setFilter(null)} label="All sources" />
          {SOURCES.map((s) => (
            <FilterChip key={s.id} active={filter === s.id} onClick={() => setFilter(s.id)} label={s.label} colorClass={s.color} />
          ))}
        </div>
      </div>

      {/* ─── Editor (slide-in style at top of list) ───────────────────── */}
      {(showCreate || editing) && (
        <EntryEditor
          initialEntry={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={async () => { setShowCreate(false); setEditing(null); await load(); }}
        />
      )}

      {/* ─── Entry list / empty state ─────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-emerald-400" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} hasSearch={!!search} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {entries.map((e) => (
            <EntryCard
              key={e.tradeId}
              entry={e}
              search={searchMode ? search : null}
              onEdit={() => setEditing(e.tradeId)}
              onDelete={() => handleDelete(e.tradeId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function HeroStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-dark-900/40 backdrop-blur-sm rounded-xl border border-dark-700/40 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent || 'text-gray-400'}`} />
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      </div>
      <p className={`text-xl font-bold font-mono ${accent || 'text-white'}`}>{value}</p>
    </div>
  );
}

function FilterChip({ active, onClick, label, colorClass }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-all flex-shrink-0 ${
        active
          ? colorClass
            ? colorClass.replace('/10', '/15')
            : 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300'
          : 'bg-dark-800/40 border-dark-700/50 text-gray-400 hover:border-dark-600 hover:text-white'
      }`}
    >
      {label}
    </button>
  );
}

function EmptyState({ onCreate, hasSearch }) {
  return (
    <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-12 text-center">
      <div className="w-16 h-16 rounded-2xl bg-dark-700 mx-auto mb-4 flex items-center justify-center">
        <BookOpen className="w-8 h-8 text-gray-600" />
      </div>
      <p className="text-white font-medium mb-1">
        {hasSearch ? 'No entries match your search' : 'No journal entries yet'}
      </p>
      <p className="text-sm text-gray-500 mb-4">
        {hasSearch
          ? 'Try a different keyword, or clear the search to see all entries.'
          : 'Record why you entered, what happened, and what you learned — across every trading system you use.'}
      </p>
      {!hasSearch && (
        <button
          onClick={onCreate}
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm px-4 py-2 rounded-lg"
        >
          <Plus className="w-4 h-4" /> Write first entry
        </button>
      )}
    </div>
  );
}

function EntryCard({ entry, search, onEdit, onDelete }) {
  const sentiment = SENTIMENT_CONFIG[entry.sentiment] || SENTIMENT_CONFIG.neutral;
  const SentIcon = sentiment.Icon;
  const source = SOURCE_BY_ID[entry.source] || SOURCE_BY_ID.other;
  // When in search mode, prefer the server-built snippet so the matched
  // keyword is visible even if it's deep in a long note.
  const previewText = search && entry.snippet ? entry.snippet : (entry.notePreview || entry.note || '');
  const pnl = entry.realizedPnl;

  return (
    <div className={`group relative bg-dark-800/40 rounded-2xl border border-dark-700/50 border-l-2 ${sentiment.border} p-4 hover:bg-dark-800/60 transition-colors`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <SentIcon className={`w-4 h-4 ${sentiment.color} flex-shrink-0`} />
          <span className="text-sm font-bold text-white font-mono truncate max-w-[14ch]">
            {entry.symbol || entry.tradeId?.slice(0, 12)}
          </span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${source.color}`}>
            {source.label}
          </span>
          {pnl != null && pnl !== 0 && (
            <span className={`text-xs font-mono font-bold ${pnl > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {pnl >= 0 ? '+' : '-'}${Math.abs(pnl).toFixed(2)}
            </span>
          )}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={onEdit} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-dark-700/40">
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-dark-700/40">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Note / search snippet */}
      <p className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
        {search ? highlightMatch(previewText, search) : previewText}
      </p>

      {/* Lesson */}
      {entry.lessonLearned && (
        <div className="mt-3 flex items-start gap-1.5 text-xs text-amber-300/80 bg-amber-500/5 rounded-lg p-2 border border-amber-500/15">
          <Lightbulb className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span className="italic">{search ? highlightMatch(entry.lessonLearned, search) : entry.lessonLearned}</span>
        </div>
      )}

      {/* Tags */}
      {entry.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {entry.tags.map((t) => (
            <span key={t} className="text-[10px] bg-dark-700/50 text-gray-300 px-2 py-0.5 rounded flex items-center gap-0.5">
              <Hash className="w-2.5 h-2.5 text-gray-500" />
              {search ? highlightMatch(t, search) : t}
            </span>
          ))}
        </div>
      )}

      {/* Footer: timestamp + matched-in indicator */}
      <div className="flex items-center justify-between mt-3 text-[10px] text-gray-600">
        <span className="flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {new Date(entry.updatedAt).toLocaleString()}
        </span>
        {search && entry.matchedIn && (
          <span className="text-amber-400/70 font-mono">match in {entry.matchedIn}</span>
        )}
      </div>
    </div>
  );
}

function EntryEditor({ initialEntry, onClose, onSaved }) {
  const [tradeId, setTradeId] = useState(initialEntry || '');
  const [source, setSource] = useState('other');
  const [symbol, setSymbol] = useState('');
  const [note, setNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [sentiment, setSentiment] = useState('neutral');
  const [lessonLearned, setLessonLearned] = useState('');
  const [realizedPnl, setRealizedPnl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!!initialEntry);

  // Autocomplete for trade picker
  const [recentTrades, setRecentTrades] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const tradeIdInputRef = useRef(null);

  useEffect(() => {
    if (!initialEntry) return;
    (async () => {
      try {
        const res = await getJournalEntry(initialEntry);
        const e = res?.data || res;
        if (e) {
          setSource(e.source || 'other');
          setSymbol(e.symbol || '');
          setNote(e.note || '');
          setTags(e.tags || []);
          setSentiment(e.sentiment || 'neutral');
          setLessonLearned(e.lessonLearned || '');
          setRealizedPnl(e.realizedPnl != null ? String(e.realizedPnl) : '');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [initialEntry]);

  // Lazy-load recent trades when the picker opens
  async function openPicker() {
    if (initialEntry) return; // editing existing — don't allow tradeId change
    setShowPicker(true);
    if (recentTrades.length === 0 && !pickerLoading) {
      setPickerLoading(true);
      try {
        const res = await getJournalRecentTrades();
        setRecentTrades(res?.data?.trades || []);
      } catch {} finally { setPickerLoading(false); }
    }
  }
  function selectTrade(t) {
    setTradeId(t.tradeId);
    if (t.source) setSource(t.source);
    if (t.symbol) setSymbol(t.symbol);
    if (typeof t.realizedPnl === 'number') {
      setRealizedPnl(String(t.realizedPnl));
      setSentiment(t.realizedPnl > 0 ? 'positive' : t.realizedPnl < 0 ? 'negative' : 'neutral');
    }
    setShowPicker(false);
  }

  function addTag() {
    const t = tagInput.trim().slice(0, 32);
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput('');
  }

  async function handleSave() {
    if (!tradeId) { setError('Trade ID is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await upsertJournalEntry(tradeId, {
        source,
        symbol,
        note,
        tags,
        sentiment,
        lessonLearned,
        realizedPnl: realizedPnl ? parseFloat(realizedPnl) : null,
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
      </div>
    );
  }

  const filteredTrades = tradeId
    ? recentTrades.filter(
        (t) =>
          t.tradeId.toLowerCase().includes(tradeId.toLowerCase()) ||
          (t.label || '').toLowerCase().includes(tradeId.toLowerCase())
      )
    : recentTrades;

  return (
    <div className="bg-gradient-to-br from-emerald-500/5 to-dark-800/60 rounded-2xl border border-emerald-500/30 p-5 space-y-4 shadow-xl shadow-emerald-500/5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          {initialEntry ? (
            <><Edit2 className="w-4 h-4 text-emerald-400" /> Edit entry</>
          ) : (
            <><Plus className="w-4 h-4 text-emerald-400" /> New journal entry</>
          )}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Trade ID with autocomplete picker */}
      <div className="relative">
        <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
          Trade / Bot ID
          {!initialEntry && (
            <button
              type="button"
              onClick={openPicker}
              className="text-[10px] text-emerald-400 hover:text-emerald-300 ml-auto flex items-center gap-1"
            >
              <Link2 className="w-3 h-3" /> Pick from recent closed trades
            </button>
          )}
        </label>
        <div className="relative">
          <input
            ref={tradeIdInputRef}
            value={tradeId}
            onChange={(e) => setTradeId(e.target.value)}
            onFocus={() => !initialEntry && openPicker()}
            disabled={!!initialEntry}
            placeholder="e.g. abc12345 or click to pick from recent trades"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 pr-8 text-white text-sm font-mono disabled:opacity-50"
          />
          {!initialEntry && (
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          )}
        </div>

        {showPicker && !initialEntry && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowPicker(false)} />
            <div className="absolute z-50 left-0 right-0 mt-1 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl max-h-72 overflow-y-auto">
              {pickerLoading ? (
                <div className="p-4 text-center text-gray-500 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1 text-emerald-400" /> Loading recent trades…
                </div>
              ) : filteredTrades.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-xs">
                  {tradeId
                    ? 'No recent closed trades match. You can also type a custom ID below.'
                    : 'No closed trades yet. You can still type a custom ID above.'}
                </div>
              ) : (
                <>
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold border-b border-dark-700/50">
                    Recent closed trades — click to populate fields
                  </div>
                  {filteredTrades.slice(0, 12).map((t) => {
                    const src = SOURCE_BY_ID[t.source] || SOURCE_BY_ID.other;
                    return (
                      <button
                        key={`${t.source}:${t.tradeId}`}
                        type="button"
                        onClick={() => selectTrade(t)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-dark-700/40 text-left border-b border-dark-700/30 last:border-0"
                      >
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${src.color} flex-shrink-0`}>
                          {src.label.split(' ')[0]}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono text-white truncate">{t.label}</p>
                          <p className="text-[10px] text-gray-500 truncate">{t.tradeId}</p>
                        </div>
                        {t.realizedPnl != null && (
                          <span className={`text-[10px] font-mono font-bold flex-shrink-0 ${t.realizedPnl > 0 ? 'text-emerald-400' : t.realizedPnl < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                            {t.realizedPnl >= 0 ? '+' : '-'}${Math.abs(t.realizedPnl).toFixed(2)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Source</label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm"
          >
            {SOURCES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Symbol</label>
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="BTC/USDT"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">Realized P&L ($)</label>
        <input
          type="number"
          step="any"
          value={realizedPnl}
          onChange={(e) => setRealizedPnl(e.target.value)}
          className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono"
        />
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">
          Note <span className="text-gray-600">— why you entered, what happened, what to repeat</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 5000))}
          rows={5}
          placeholder="What was your thesis? What did the market do? Where did you exit and why?"
          className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm resize-none focus:border-emerald-500/40 focus:outline-none transition-colors"
        />
        <p className="text-[10px] text-gray-500 mt-1 text-right">{note.length}/5000</p>
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
          <Lightbulb className="w-3 h-3 text-amber-400" /> Lesson learned <span className="text-gray-600">— one line you'd tell yourself next time</span>
        </label>
        <input
          value={lessonLearned}
          onChange={(e) => setLessonLearned(e.target.value.slice(0, 500))}
          placeholder="What would you do differently?"
          className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">Tags</label>
        <div className="flex gap-2">
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
            placeholder="Add tag (Enter to confirm)"
            className="flex-1 bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm"
          />
          <button onClick={addTag} className="px-3 py-2 rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500 text-sm">
            Add
          </button>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {tags.map((t) => (
              <span key={t} className="text-xs bg-dark-700 text-gray-300 px-2 py-0.5 rounded flex items-center gap-1">
                <Hash className="w-2.5 h-2.5 text-gray-500" />{t}
                <button onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">Sentiment</label>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(SENTIMENT_CONFIG).map(([s, cfg]) => {
            const Icon = cfg.Icon;
            const active = sentiment === s;
            return (
              <button
                key={s}
                onClick={() => setSentiment(s)}
                className={`p-2.5 rounded-lg border flex items-center justify-center gap-1.5 capitalize transition-all ${
                  active ? `${cfg.color} ${cfg.bg} border-current/40` : 'bg-dark-700 border-dark-600 text-gray-500 hover:border-dark-500'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs font-medium">{s}</span>
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs">{error}</div>
      )}

      <div className="flex gap-2 pt-2">
        <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-dark-600 text-gray-300 hover:bg-dark-700 text-sm">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-dark-900 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-emerald-500/20"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save entry'}
        </button>
      </div>
    </div>
  );
}
